import React, { type ComponentProps, useMemo } from 'react';
import { Link } from 'expo-router';
import { Pressable } from 'react-native';

import { ReorderRail } from '@sg/shared-fe/features/menu';

import { MenuReorderRailCard } from './components';

// ─────────────────────────────────────────────────────────────────────────────

export const MenuReorderRail = (props: Props) => {
  const {
    lineItems,
    mostRecentOrder,
    restaurantId,
    restaurantName,
    restaurantSlug,
    deliveryDetails,
  } = props;

  // ─── Derived ─────────────────────────────────────────────────────────

  const deliveryOrderDetails = useMemo(() => {
    if (!deliveryDetails) return;

    return {
      tip: deliveryDetails.tip,
      deliveryFee: deliveryDetails.deliveryFee,
      vendor: deliveryDetails.vendor,
      vendorRestaurantId: deliveryDetails.vendorRestaurantId,
      address: { id: deliveryDetails.addressId },
    };
  }, [deliveryDetails]);

  const targetRestaurant = useMemo(
    () => ({ restaurantId, restaurantName, restaurantSlug, deliveryOrderDetails }),
    [restaurantId, restaurantName, restaurantSlug, deliveryOrderDetails],
  );

  const reorderModalParams = useMemo(
    () => ({
      restaurantId,
      ...(deliveryDetails ? { addressId: deliveryDetails.addressId } : {}),
    }),
    [restaurantId, deliveryDetails],
  );

  // ─────────────────────────────────────────────────────────────────────

  return (
    <ReorderRail.Container>
      <ReorderRail.Header>
        <ReorderRail.Heading />

        <Link href={{ pathname: '/reorder', params: reorderModalParams }} asChild>
          <Pressable accessibilityRole="link">
            <ReorderRail.SeeAll />
          </Pressable>
        </Link>
      </ReorderRail.Header>

      <ReorderRail.List>
        {lineItems.map((lineItem) => (
          <MenuReorderRailCard
            key={lineItem.id}
            lineItem={lineItem}
            mostRecentOrder={mostRecentOrder}
            targetRestaurant={targetRestaurant}
          />
        ))}
      </ReorderRail.List>
    </ReorderRail.Container>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type Props = {
  lineItems: MenuReorderRailCardProps['lineItem'][];
  mostRecentOrder: MenuReorderRailCardProps['mostRecentOrder'];
  restaurantId: string;
  restaurantName: string;
  restaurantSlug: string;
  deliveryDetails?: {
    addressId: string;
    tip: number;
    deliveryFee: number;
    vendor: string;
    vendorRestaurantId: string;
  };
};

type MenuReorderRailCardProps = ComponentProps<typeof MenuReorderRailCard>;
